import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Badge,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { getAuthUserString } from "../../helpers/auth_storage";
import { Link, useSearchParams } from "react-router-dom";
import withRouter from "../../components/Common/withRouter";
import Breadcrumb from "../../components/Common/Breadcrumb";
import avatar from "../../assets/images/users/avatar-1.jpg";
import { editProfile, resetProfileFlag } from "../../store/actions";
import { createSelector } from "reselect";
import { useAuth } from "../../contexts/AuthContext";
import { fetchViewerMfaStatus } from "../../helpers/wordpress_totp";

const UserProfile = () => {
  const dispatch = useDispatch();
  const { user: authCtxUser } = useAuth();
  const [searchParams] = useSearchParams();

  const userprofileData = createSelector(
    (state: any) => state.profile,
    (state) => ({
      error: state.error,
      success: state.success,
    })
  );
  const { error, success } = useSelector(userprofileData);

  const [email, setemail] = useState<string>("");
  const [name, setname] = useState<string>("");
  const [idx, setidx] = useState<string>("");
  const [totpEnabled, setTotpEnabled] = useState<boolean | null>(null);
  const [emailOtpEnabled, setEmailOtpEnabled] = useState(true);
  const [totpLoading, setTotpLoading] = useState(true);

  useEffect(() => {
    const authUser: any = getAuthUserString();
    if (authUser) {
      try {
        const obj = JSON.parse(authUser);
        if (process.env.REACT_APP_DEFAULTAUTH === "wordpress") {
          setname(obj.user?.name || authCtxUser?.name || "");
          setemail(obj.user?.email || authCtxUser?.email || "");
          setidx(String(obj.user?.id || authCtxUser?.id || ""));
        } else if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
          setname(obj.displayName);
          setemail(obj.email);
          setidx(obj.uid);
        } else {
          setname(obj.username || obj.user?.name || "");
          setemail(obj.email || obj.user?.email || "");
          setidx(String(obj.uid || obj.user?.id || ""));
        }
      } catch {
        // ignore
      }
      setTimeout(() => {
        dispatch(resetProfileFlag());
      }, 3000);
    }
  }, [dispatch, success, authCtxUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTotpLoading(true);
      try {
        const status = await fetchViewerMfaStatus();
        if (!cancelled) {
          setTotpEnabled(status.totpEnabled);
          setEmailOtpEnabled(status.emailOtpEnabled);
        }
      } catch {
        if (!cancelled) {
          setTotpEnabled(false);
          setEmailOtpEnabled(true);
        }
      } finally {
        if (!cancelled) setTotpLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: name || "",
      idx: idx || "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please Enter Your UserName"),
    }),
    onSubmit: (values) => {
      dispatch(editProfile(values));
    },
  });

  document.title = "Profile | NGK Tours";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="NGK Tours" breadcrumbItem="Profile" />

          <Row>
            <Col lg="12">
              {error ? <Alert color="danger">{error}</Alert> : null}
              {success ? <Alert color="success">{success}</Alert> : null}
              {searchParams.get("totp") === "1" && (
                <Alert color="success">
                  Authenticator 2FA is enabled. See status below.
                </Alert>
              )}

              <Card>
                <CardBody>
                  <div className="d-flex">
                    <div className="ms-3">
                      <img
                        src={avatar}
                        alt=""
                        className="avatar-md rounded-circle img-thumbnail"
                      />
                    </div>
                    <div className="flex-grow-1 align-self-center ms-3">
                      <div className="text-muted">
                        <h5>{name || "Staff user"}</h5>
                        <p className="mb-1">{email}</p>
                        <p className="mb-0">Id: {idx || "—"}</p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h4 className="card-title mb-4">Security — Authentication</h4>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">Email OTP</h5>
                  <p className="text-muted mb-0 small">
                    Code sent to your email at login (when enabled)
                  </p>
                </div>
                {totpLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Badge color={emailOtpEnabled ? "success" : "secondary"} pill>
                    {emailOtpEnabled ? "Enabled" : "Off"}
                  </Badge>
                )}
              </div>

              <hr />

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">Authenticator app (2FA)</h5>
                  <p className="text-muted mb-0 small">
                    Google Authenticator / Authy
                  </p>
                </div>
                {totpLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Badge color={totpEnabled ? "success" : "warning"} pill>
                    {totpEnabled ? "Enabled" : "Not set up"}
                  </Badge>
                )}
              </div>

              {!totpLoading && totpEnabled && (
                <Alert color="success" className="mb-3">
                  Authenticator 2FA is active on this account.
                </Alert>
              )}
              {!totpLoading && !totpEnabled && (
                <Alert color="warning" className="mb-3">
                  Authenticator 2FA is not set up yet.
                </Alert>
              )}

              <div className="d-flex flex-wrap gap-2">
                {!totpEnabled && (
                  <Button color="primary" tag={Link} to="/totp-setup">
                    Set up authenticator
                  </Button>
                )}
                <Button color="light" tag={Link} to="/security-settings">
                  Manage MFA settings
                </Button>
              </div>

              <p className="small text-muted mt-3 mb-0">
                At least one of Email OTP or Authenticator 2FA must stay on.
                Manage both from Security Settings.
              </p>
            </CardBody>
          </Card>

          <h4 className="card-title mb-4 mt-4">Change User Name</h4>
          <Card>
            <CardBody>
              <Form
                className="form-horizontal"
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="form-group">
                  <Label className="form-label">User Name</Label>
                  <Input
                    name="username"
                    className="form-control"
                    placeholder="Enter User Name"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.username || ""}
                    invalid={
                      !!(
                        validation.touched.username &&
                        validation.errors.username
                      )
                    }
                  />
                  {validation.touched.username && validation.errors.username ? (
                    <FormFeedback type="invalid">
                      {validation.errors.username}
                    </FormFeedback>
                  ) : null}
                  <Input name="idx" value={idx} type="hidden" />
                </div>

                <div className="text-center mt-4">
                  <Button type="submit" color="danger">
                    Update User Name
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
